import { css } from '@emotion/react';
import { type FC } from 'react';
import {
  ButtonComponent, type ButtonProps,
  IconButtonComponent, type IconButtonProps,
} from '~/_shared/baseComponents/buttons';
import { LottieAnimationComponent } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.component';
import {
  type LottieAnimationConfig, type LottieAnimationSegment, type LottieAnimationViewDetails,
} from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';

const textStyle = ({ color, isBold }: {color?: string; isBold?: boolean}) => css({
  color,
  fontSize: 16,
  fontWeight: isBold ? 'bold' : 'normal',
});

const textContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
});

const containerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const leftSideStyle = css({
  display: 'flex',
  gap: 16,
  alignItems: 'center',
});

const rightSideStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const verticalSplitStyle = css({
  display: 'flex',
  flexDirection: 'column',
});

const oneLineStyle = css({
  display: 'flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  gap: 4,
});

export type TopUnderbarSavingStatusComponentProps = {
  className?: string;
  animation?: LottieAnimationConfig & {
    autoplay?: boolean;
    loop?: boolean;
    bothDirections?: boolean;
    progress?: number;
    speed?: number;
    segment?: LottieAnimationSegment;
    view?: LottieAnimationViewDetails;
  };
  text: {
    color?: string;
    value: string;
  };
  suffixText?: {
    color?: string;
    value: string;
  };
  subtexts?: ReadonlyArray<{
    color?: string;
    value: string;
  }>;
  button?: ButtonProps & { tooltip?: string };
  iconButton?: IconButtonProps;
};

export const TopUnderbarStatusItemComponent: FC<TopUnderbarSavingStatusComponentProps> = (props) => {
  const { text, animation, suffixText, subtexts, className } = props;

  return (
    <div
      css={containerStyle}
      className={className}
    >
      <div css={leftSideStyle}>
        <div css={verticalSplitStyle}>
          <div css={oneLineStyle}>
            <div css={textStyle({ color: text.color, isBold: true })}>
              {text.value}
            </div>
            {animation && (
              <LottieAnimationComponent
                css={{ flexShrink: 0 }}
                size={20}
                {...animation}
              />
            )}
            {suffixText &&
              <span css={[textStyle({ color: suffixText.color }), { flexShrink: 10 }]}>{suffixText.value}</span>
            }
          </div>
          <div>
            {subtexts && (
              <div css={textContainerStyle}>
                {subtexts.map((text, index) => (
                  <div key={index} css={textStyle({ color: text.color })}>
                    {text.value && text.value}
                    {text.value === '' && <span>&nbsp;</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div css={rightSideStyle}>
        {props.button && (
          <TooltipComponent
            behavior={TooltipBehavior.ShowAlways}
            tooltipContent={props.button.tooltip}
          >
            <ButtonComponent
              {...props.button}
            />
          </TooltipComponent>
        )}
        {props.iconButton && (
          <IconButtonComponent
            {...props.iconButton}
          />
        )}
      </div>
    </div>
  );
};
